import _ from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useDispatch, useSelector } from 'react-redux';
import { titleCase } from 'title-case';
import { fontsApi } from '../../../../../../api/fonts';
import {
  selectCurrPage,
  selectFieldSettings,
  selectGlobalFont,
  selectParagraphFont,
  setFieldSettings,
} from '../../../../../../store/reducers/forms';
import { createWeightList } from '../../../../../../utils/fonts';
import ModalSettings from '../../../Components/ModalSettings';
import settingsStyles from '../../../Components/ModalSettings/ModalSettings.module.scss';
import Switch from '../../../Components/ModalSettings/Switch';
import WeightButton from '../../../Components/WeightButton';
import formStyles from '../../FormSettings.module.scss';
import styles from './OptInSettings.module.scss';

const OptInSettings = ({ activeSettings }) => {
  const quillRef = useRef(null);
  const initialized = useRef(false);
  const dispatch = useDispatch();
  const currPage = useSelector(selectCurrPage);
  const fieldSettings = useSelector(selectFieldSettings);
  const [content, setContent] = useState(
    '<p>I have read and accept the terms and conditions.</p>'
  );
  const [align, setAlign] = useState('left');
  const [required, setRequired] = useState(true);
  const debouncedUpdateStoreRef = useRef();
  const [errors, setErrors] = useState({});
  const globalFont = useSelector(selectGlobalFont);
  const paragraphFont = useSelector(selectParagraphFont);
  const font = paragraphFont || globalFont;
  const [weights, setWeights] = useState([]);

  const { currentData: fontsData } = fontsApi.useGetFontsForTeamQuery();

  useEffect(() => {
    if (fontsData && fontsData.fonts?.length > 0) {
      const fontList = fontsData.fonts.filter((fd) => fd.name === font);
      const weightList = [...new Set(fontList.map((fd) => fd.weight))].sort(
        (a, b) => a - b
      );
      setWeights(createWeightList(weightList));
    }
  }, [fontsData]);

  const updateStore = useCallback(async () => {
    const obj = {
      name: 'optIn',
      content,
      align,
      type: 'optIn',
      required,
    };
    dispatch(setFieldSettings({ ...obj, field_id: activeSettings.field_id }));
  }, [content, align, required, dispatch, activeSettings]);

  useEffect(() => {
    if (debouncedUpdateStoreRef.current) {
      debouncedUpdateStoreRef.current.cancel();
    }

    debouncedUpdateStoreRef.current = _.debounce(updateStore, 300);
    debouncedUpdateStoreRef.current();

    return () => {
      if (debouncedUpdateStoreRef.current) {
        debouncedUpdateStoreRef.current.cancel();
      }
    };
  }, [content, align, required, updateStore]);

  const handleContentChange = (value) => {
    const textContent = value.replace(/<[^>]+>/g, '');
    if (textContent.length <= 1000) {
      setContent(value);
      if (errors.content) {
        setErrors({});
      }
    } else {
      const truncatedContent = textContent.slice(0, 1000);
      setContent(truncatedContent);
      setErrors({ ...errors, content: 'Character limit of 1000 reached' });
    }
  };

  const modules = {
    toolbar: [
      [
        'bold',
        'italic',
        'underline',
        { list: 'ordered' },
        { list: 'bullet' },
        'link',
      ],
    ],
  };

  const formats = [
    'bold',
    'italic',
    'underline',
    'list',
    'link',
    'font-weight',
  ];

  const setOptions = (config) => {
    setContent(config.content);
    setAlign(config.align);
    setRequired(config.required);
  };

  useEffect(() => {
    if (initialized.current) return;
    const currPageSettings = fieldSettings.find((o) => o.pageName === currPage);
    if (currPageSettings) {
      const config = currPageSettings.fields?.find(
        (x) => x.field_id === activeSettings.field_id
      );
      if (config) {
        setOptions(config);
      } else {
        handleContentChange(content);
      }
    }
    initialized.current = true;
  }, [activeSettings, currPage, fieldSettings]);

  const handleClick = (e) => {
    if (
      !e.target.closest('.ql-container') &&
      !e.target.closest('.ql-toolbar')
    ) {
      if (quillRef.current) {
        quillRef.current.focus();
        const quill = quillRef.current.getEditor();
        quill.setSelection(quill.getLength());
      }
    }
  };

  useEffect(() => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      const quillContainer = editor.root;
      quillContainer.style.fontFamily = font;
    }
  }, [font]);

  useEffect(() => {
    if (weights?.length > 2 && quillRef.current) {
      const boldButton = document.querySelector('.ql-bold');
      if (boldButton) {
        const weightButtonContainer = document.createElement('button');
        weightButtonContainer.className = 'ql-weight';
        boldButton.replaceWith(weightButtonContainer);
        ReactDOM.render(
          <WeightButton weights={weights} quill={quillRef.current} />,
          weightButtonContainer
        );
      }
    }
  }, [weights, quillRef.current]);

  return (
    <ModalSettings title="Opt-in Properties">
      <Switch
        text="Required Field"
        checked={required}
        onChange={() => {
          setRequired(!required);
        }}
      />

      <div className={styles.wrapper}>
        <div className={settingsStyles.title}>{titleCase('Opt-in text')}</div>
        <div role="none" onClick={handleClick}>
          <ReactQuill
            ref={quillRef}
            formats={formats}
            className={styles.quillContainer}
            theme="snow"
            value={content}
            onChange={handleContentChange}
            modules={modules}
          />
        </div>
        {errors.content && (
          <div className={`${formStyles.error}`}>{errors.content}</div>
        )}
      </div>
    </ModalSettings>
  );
};

export default OptInSettings;
